/**
 * TestingIndexPage - Interactive Component Testing Hub
 *
 * Purpose: Central landing page for all interactive component tests
 * Access: /testing
 *
 * This page provides a directory of all available component test pages,
 * making it easy to navigate to specific testing environments.
 *
 * Pattern: As we add more test pages, add them to the testPages array below
 */

import { Badge, Box, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

interface TestPage {
  title: string;
  path: string;
  description: string;
  component: string;
  status: 'ready' | 'in-progress' | 'planned';
}

const testPages: TestPage[] = [
  {
    title: 'Project Date Inputs',
    path: '/testing/project-date-inputs',
    description:
      'Test date configuration with tax collection schedules (summer/winter), fiscal year calculations, and revenue percentage distributions.',
    component: 'ProjectDateInputs',
    status: 'ready',
  },
  {
    title: 'Taxable Value Inputs',
    path: '/testing/taxable-value-inputs',
    description: 'Test taxable value inputs, growth rates, and projections.',
    component: 'TaxableValueInputs',
    status: 'ready',
  },
];

export const TestingIndexPage = () => {
  const getStatusColor = (status: TestPage['status']) => {
    switch (status) {
      case 'ready':
        return 'green';
      case 'in-progress':
        return 'yellow';
      case 'planned':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: TestPage['status']) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'in-progress':
        return 'In Progress';
      case 'planned':
        return 'Planned';
      default:
        return status;
    }
  };

  return (
    <Box p={8} maxW="1400px" mx="auto">
      <VStack align="stretch" gap={8}>
        {/* Header */}
        <Box>
          <Heading size="2xl" mb={3}>
            Component Testing Hub
          </Heading>
          <Text color="gray.600" fontSize="lg">
            Interactive testing environments for all input builder components.
          </Text>
          <Text color="gray.500" fontSize="sm" mt={2}>
            Select a component below to access its dedicated testing page.
          </Text>
        </Box>

        {/* Test Pages Grid */}
        <Box>
          <Heading size="lg" mb={4}>
            Available Test Pages
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {testPages.map((page) => (
              <Card.Root
                key={page.path}
                asChild
                variant="outline"
                _hover={{
                  borderColor: page.status === 'ready' ? 'blue.500' : 'gray.300',
                  boxShadow: page.status === 'ready' ? 'md' : 'none',
                  cursor: page.status === 'ready' ? 'pointer' : 'not-allowed',
                }}
                opacity={page.status === 'ready' ? 1 : 0.6}
              >
                <Link to={page.status === 'ready' ? page.path : '#'}>
                  <Card.Body gap={3}>
                    <HStack justify="space-between" align="flex-start">
                      <Heading size="md">{page.title}</Heading>
                      <Badge colorScheme={getStatusColor(page.status)} fontSize="xs">
                        {getStatusLabel(page.status)}
                      </Badge>
                    </HStack>

                    <Text fontSize="sm" color="gray.600">
                      {page.description}
                    </Text>

                    <Text fontSize="xs" color="gray.500" fontFamily="mono">
                      Component: {page.component}
                    </Text>
                  </Card.Body>
                </Link>
              </Card.Root>
            ))}
          </SimpleGrid>
        </Box>

        {/* Pattern Documentation */}
        <Box
          p={6}
          bg="blue.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="blue.500"
          mt={4}
        >
          <Heading size="sm" mb={3}>
            Testing Pattern
          </Heading>
          <VStack align="flex-start" fontSize="sm" gap={2}>
            <Text>
              <strong>Purpose:</strong> Each test page provides an interactive environment to
              manually verify component behavior
            </Text>
            <Text>
              <strong>Structure:</strong> Component + Test Controls + State Display + Manual
              Checklist
            </Text>
            <Text>
              <strong>Benefits:</strong> Visual regression testing, easy debugging, documents
              expected behavior
            </Text>
            <Text>
              <strong>Adding New Tests:</strong> Create a new test page in{' '}
              <code>src/pages/testing/</code>, add route in <code>routes.tsx</code>, and add entry
              to <code>testPages</code> array above
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};
